import React, { useState, useEffect, useCallback } from 'react';
import { Link, Text, Flex, theme, Button } from '@xometry/xometry_loft';
import { Cell } from 'react-table';
import Table from '../../../components/Table';
import { WWW_ORIGIN } from '../../../config';
import { Organization } from '../../../schema/generated/schema';
import OrganizationMemberHelper, {
  PAGINATION_PAGE_SIZE,
} from '../../Organization/TeamSettings/TeamMemberManagement/helper';
import ReactivateTeamModal from '../ReactivateModal';

interface InactiveTeamProps {
  isStaff: boolean;
  setReactivateTeamModalOpen: (a: boolean) => void;
  setOrgToReactivate: (org: any) => void;
  refetchInactiveOrganizations: any;
}

const InactiveTeamColumns = ({
  isStaff,
  setReactivateTeamModalOpen,
  setOrgToReactivate,
}: InactiveTeamProps) => {
  const handleReactivateModal = (organization) => {
    setOrgToReactivate(organization);
    setReactivateTeamModalOpen(true);
  };

  return [
    {
      Header: 'Team Name',
      accessor: 'name',
      Cell: ({ cell }: { cell: Cell }) => (
        <Text kind="label" padding="0px">
          {cell.value}
        </Text>
      ),
    },
    {
      Header: 'Members',
      accessor: 'activeMembers',
      Cell: ({ cell }: { cell: Cell }) => (
        <Text kind="body-sm">
          {cell.value} Member{cell.value > 1 ? 's' : ''}
        </Text>
      ),
    },
    {
      accessor: 'id',
      Cell: ({ cell }: { cell: Cell }) => (
        <Flex alignItems="center">
          <Link
            href={`${WWW_ORIGIN}/account/organization/${cell.value}/settings`}
            mr="4px"
          >
            <i className="fa-solid fa-arrow-up-right-from-square" />
            &nbsp; View Team
          </Link>
        </Flex>
      ),
    },
    {
      accessor: 'availableRoles',
      Cell: ({ cell }: { cell: Cell }) => (
        <>
          <Flex alignItems="center" justifyContent="end">
            {(cell.value.some((role) => role.name === 'Admin') || isStaff) && (
              <Button
                kind={theme.kind.primaryOutline}
                scale="small"
                onClick={() => handleReactivateModal(cell.row.original)}
              >
                Reactivate Team
              </Button>
            )}
          </Flex>
        </>
      ),
    },
  ];
};

interface InactiveTeamsTableProps {
  inactiveOrganizations: Partial<Organization[]>;
  loadingUserOrganizations: boolean;
  isStaff: boolean;
  refetchInactiveOrganizations: any;
  setSelectedTab: any;
}

const InactiveTeamsTab = ({
  inactiveOrganizations,
  loadingUserOrganizations,
  isStaff,
  refetchInactiveOrganizations,
  setSelectedTab,
}: InactiveTeamsTableProps) => {
  const [maxPages, setMaxPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [reactivateTeamModalOpen, setReactivateTeamModalOpen] = useState(false);
  const [orgToReactivate, setOrgToReactivate] = useState({});

  useEffect(() => {
    if (inactiveOrganizations.length > 0) {
      setMaxPages(
        Math.ceil(inactiveOrganizations.length / PAGINATION_PAGE_SIZE)
      );
      setCurrentMembers(inactiveOrganizations);
    }
  }, [inactiveOrganizations.length]);

  // React callback to manage fetch calls
  const fetchMemberData = useCallback(
    (currentPage = 1, pageSize = 5) => {
      OrganizationMemberHelper.fetchMembersInMemory(
        inactiveOrganizations,
        setCurrentMembers,
        currentPage,
        pageSize
      );
    },
    [selectedPage]
  );
  return (
    <>
      {inactiveOrganizations.length > 0 && (
        <Table
          data={currentMembers}
          columns={InactiveTeamColumns({
            isStaff,
            setReactivateTeamModalOpen,
            setOrgToReactivate,
          })}
          loading={loadingUserOrganizations}
          hideHeader={false}
          showSelectColumn={false}
          isReadOnly={true}
          recordPageCount={maxPages}
          pageSize={PAGINATION_PAGE_SIZE}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          fetchCallback={fetchMemberData}
        />
      )}
      <ReactivateTeamModal
        organization={orgToReactivate}
        setReactivateTeamModalOpen={setReactivateTeamModalOpen}
        reactivateTeamModalOpen={reactivateTeamModalOpen}
        refetchInactiveOrganizations={refetchInactiveOrganizations}
        setSelectedTab={setSelectedTab}
      />
    </>
  );
};

export default InactiveTeamsTab;
